import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import type { ComponentProps } from 'react'
import { useMemo } from 'react'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import VillaCardInclusions from './VillaCardInclusions'
import LEOfferPriceDetails from './LEOfferPriceDetails'
import VillaLocationCaption from './VillaLocation'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { generateLoyaltyPointsCalculatorVillasOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPriceNew'
import useSearchOfferBurnPricing from 'hooks/useSearchOfferBurnPricing'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import { VILLAS_REVIEWS_TOTAL_THRESHOLD } from 'constants/review'
import { useRebrandOverlayBookmarkProps } from 'hooks/useRebrandOverlayButtonProps'
import { isHotelPackage } from 'lib/offer/offerTypes'

type InheritedCarouselCardMediumProps = Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'>

interface Props extends InheritedCarouselCardMediumProps {
  offer: App.VillaOffer | App.VillaOfferSummary
  onClick: (isRightClick?: boolean) => void
  offerUrl: string
  className?: string
  size: 'small' | 'medium'
  filters?: App.OfferListFilters
}

function VillaOfferCard(props: Props) {
  const {
    offer,
    onClick,
    className,
    offerUrl,
    size,
    variant,
    eagerLoadImage,
    filters,
  } = props
  const { iconKind, iconVariant } = useRebrandOverlayBookmarkProps()

  const pkg = offer.lowestPricePackage
  const {
    default: defaultPricing,
    member: memberPricing,
  } = useOfferPackagePrice(pkg as App.Package, offer)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (offer.bundledWithFlightsOnly) {
      return []
    }
    const propertyFees = defaultPricing.fees?.find((fee) => fee.type === 'property')?.amount ?? 0
    return [
      generateLoyaltyPointsCalculatorVillasOptions(offer, {
        price: defaultPricing.price,
        memberPrice: memberPricing?.price,
        pkg,
        propertyFees,
        nights: pkg?.duration,
      }),
    ]
  }, [pkg, defaultPricing.price, memberPricing?.price, offer, defaultPricing.fees])

  const tileBurn = useSearchOfferBurnPricing({
    price: defaultPricing.price,
    memberPrice: memberPricing?.price,
    loyaltyProductType: getLoyaltyProductType(offer),
    mx: getHotelEncryptedMargin(offer.type, isHotelPackage(pkg) ? pkg : undefined),
    viewPriceInPoints: !!filters?.viewPriceInPoints,
    disabled: !defaultPricing.price || !!offer.bundledWithFlightsOnly,
  })

  if (offer.isSoldOut) return null

  if (size === 'medium') {
    return <CarouselCardMedium
      variant={variant}
      className={className}
      eagerLoadImage={eagerLoadImage}
      bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" iconKind={iconKind} iconVariant={iconVariant} />}
      image={offer.image}
      images={offer.images}
      location={<VillaLocationCaption offer={offer} />}
      title={offer.name}
      priceDetails={<LEOfferPriceDetails variant="condensed" offer={offer} filters={filters} />}
      to={offerUrl}
      rating={isOfferRatingDisplayable(offer.property.rating, VILLAS_REVIEWS_TOTAL_THRESHOLD) ? offer.property.rating : undefined}
      description={<VillaCardInclusions offer={offer} offerUrl={offerUrl} />}
      urgencyLabels={<LabelGroup>
        <OfferLabels offer={offer} />
        {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
      </LabelGroup>}
      onClick={onClick}
      loyaltyPointsElement={<LoyaltyPointsEarn
        calculationRequests={pointsEarnCalculationRequests}
        calculationType="estimate"
        pointsToApply={tileBurn.pointsToApplyForEarn}
        burnPriceInPoints={tileBurn.priceInPointsForEarn}
      />}
    />
  } else if (size === 'small') {
    return <CarouselCardSmall
      variant={variant}
      className={className}
      eagerLoadImage={eagerLoadImage}
      bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" iconKind={iconKind} iconVariant={iconVariant} />}
      description={<VillaCardInclusions offer={offer} offerUrl={offerUrl} />}
      image={offer.image}
      images={offer.images}
      location={<VillaLocationCaption offer={offer} />}
      title={offer.property.name}
      priceDetails={<LEOfferPriceDetails offer={offer} filters={filters} />}
      to={offerUrl}
      rating={isOfferRatingDisplayable(offer.property.rating, VILLAS_REVIEWS_TOTAL_THRESHOLD) ? offer.property.rating : undefined}
      urgencyLabels={<LabelGroup>
        <OfferLabels offer={offer} />
        {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
      </LabelGroup>}
      onClick={onClick}
      loyaltyPointsElement={<LoyaltyPointsEarn
        calculationRequests={pointsEarnCalculationRequests}
        calculationType="estimate"
        pointsToApply={tileBurn.pointsToApplyForEarn}
        burnPriceInPoints={tileBurn.priceInPointsForEarn}
      />}
    />
  }
  return null
}

export default VillaOfferCard
